import type { ChangeEvent } from "react";
import { useEffect, useMemo, useState } from "react";

import { debounce } from "lodash";

import { SWReactIcons } from "@similarweb/icons";

import { removeProtocolFromUrlLikeString } from "app/pages/sales-intelligence/utils/strings";

import { StyledSearchInputContainer, StyledInput, StyledClearIconContainer, StyledBoxWrapper } from "./styles";

type SearchTextBoxProps = {
    searchText: string;
    placeholder: string;
    isDisabled?: boolean;
    className?: string;
    debounceTime?: number;
    onChange: (searchText: string) => void;
    onClear?: (clearedString: string) => void;
    preProcessValue?: (value: string) => string;
};

const SearchTextBox = (props: SearchTextBoxProps) => {
    const {
        searchText,
        placeholder,
        className,
        isDisabled = false,
        debounceTime = 600,
        onChange,
        onClear,
        preProcessValue = (v) => v,
    } = props;
    const [currentValue, setCurrentValue] = useState(searchText);
    const [isFocused, setIsFocused] = useState(false);

    const debouncedChangeHandler = useMemo(() => {
        if (debounceTime === 0) {
            return onChange;
        }

        return debounce(onChange, debounceTime);
    }, [debounceTime, onChange]);

    const handleClearSearch = () => {
        if (typeof onClear === "function") {
            onClear(currentValue);
        }

        onChange("");
    };

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        if (isDisabled) {
            return;
        }

        const {
            target: { value },
        } = event;
        const processedValue = preProcessValue(value);

        if (processedValue !== currentValue) {
            setCurrentValue(processedValue);
            debouncedChangeHandler(processedValue);
        }
    };

    const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
        if (isDisabled) {
            return;
        }

        event.preventDefault();

        const pastedText = event.clipboardData.getData("text");
        const normalisedText = preProcessValue(removeProtocolFromUrlLikeString(pastedText.trim()));

        setCurrentValue(normalisedText);
        onChange(normalisedText);
    };

    useEffect(() => {
        if (searchText !== currentValue) {
            setCurrentValue(searchText);
        }
    }, [searchText]);

    return (
        <StyledBoxWrapper className={className}>
            <StyledSearchInputContainer
                isDisabled={isDisabled}
                isFocused={isFocused}
            >
                <SWReactIcons
                    iconName="search"
                    size="sm"
                />
                <StyledInput
                    data-automation="results-search-field"
                    type="text"
                    value={currentValue}
                    onPaste={handlePaste}
                    disabled={isDisabled}
                    onChange={handleChange}
                    placeholder={placeholder}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                />
                {currentValue.length > 0 ? (
                    <StyledClearIconContainer onClick={handleClearSearch}>
                        <SWReactIcons
                            iconName="clear"
                            size="xs"
                        />
                    </StyledClearIconContainer>
                ) : null}
            </StyledSearchInputContainer>
        </StyledBoxWrapper>
    );
};

export default SearchTextBox;
